import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { EmptyState } from "../../../../common/comms/EmptyState";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const ORANGE = "#FF6414";
const BLUE = "#3B82F6";
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "rgba(226,232,240,0.6)";
const MAX_TICKS = 8;

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Short x-axis label for a bucket date. Parsed manually (no `Date`) to avoid
 * timezone drift on date-only strings — same approach as `CoachTrendCharts`,
 * including its week labels: ["Week 1", "Jul 7"], numbered from the oldest
 * bucket and stacked over the start date.
 * @param {string|null|undefined} dateStr
 * @param {'day'|'week'} granularity
 * @param {number} index - Position of the bucket in the series (0 = oldest).
 */
function formatDateLabel(dateStr, granularity, index) {
  if (!dateStr) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr));
  if (!match) return String(dateStr);
  const month = MONTH_ABBR[Number(match[2]) - 1] ?? match[2];
  const short = `${month} ${Number(match[3])}`;
  return granularity === "week" ? [`Week ${index + 1}`, short] : short;
}

/** Finite number or null, so a missing field renders as a gap rather than a zero. */
function numOrNull(value) {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Volume vs. reach: messages sent on the left axis, distinct clients messaged
 * on the right.
 *
 * The two share an x-axis but not a scale, and the right axis is pinned to the
 * roster size so "clients reached" is always read against the whole roster —
 * an auto-scaled right axis would make 20 of 168 clients look like full
 * coverage.
 *
 * @param {Object} props
 * @param {Array<Object>|null|undefined} props.series
 * @param {'day'|'week'} props.granularity
 * @param {number|null|undefined} props.totalRooms - Roster size; caps the right axis.
 */
export function ChatVolumeChart({ series, granularity, totalRooms }) {
  const rows = Array.isArray(series) ? series : [];

  const chart = useMemo(() => {
    if (rows.length === 0) return null;
    const labels = rows.map((r, index) => formatDateLabel(r?.date, granularity, index));
    const reachMax = numOrNull(totalRooms);

    return {
      data: {
        labels,
        datasets: [
          {
            label: "Messages sent",
            data: rows.map((r) => numOrNull(r?.messages_sent)),
            borderColor: ORANGE,
            backgroundColor: ORANGE,
            pointBackgroundColor: ORANGE,
            borderWidth: 2,
            pointRadius: 2,
            spanGaps: true,
            tension: 0.25,
            yAxisID: "y",
          },
          {
            label: "Clients they spoke to",
            data: rows.map((r) => numOrNull(r?.active_rooms)),
            borderColor: BLUE,
            backgroundColor: BLUE,
            pointBackgroundColor: BLUE,
            borderWidth: 2,
            pointRadius: 2,
            spanGaps: true,
            tension: 0.25,
            yAxisID: "y1",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 8,
              boxHeight: 8,
              usePointStyle: true,
              padding: 12,
              font: { size: 11 },
            },
          },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                ctx.dataset.yAxisID === "y1" && reachMax
                  ? ` ${ctx.dataset.label}: ${ctx.parsed.y} of ${reachMax}`
                  : ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
            },
          },
        },
        scales: {
          x: {
            ticks: { maxTicksLimit: MAX_TICKS, font: { size: 10 }, color: AXIS_COLOR },
            grid: { display: false },
          },
          y: {
            position: "left",
            beginAtZero: true,
            ticks: { font: { size: 10 }, color: AXIS_COLOR, precision: 0 },
            grid: { color: GRID_COLOR },
          },
          y1: {
            position: "right",
            beginAtZero: true,
            // Only pin when the roster size is known; a hardcoded fallback
            // would misstate coverage on a payload that omits it.
            ...(reachMax ? { max: reachMax } : {}),
            ticks: { font: { size: 10 }, color: AXIS_COLOR, precision: 0 },
            grid: { drawOnChartArea: false },
          },
        },
      },
    };
  }, [rows, granularity, totalRooms]);

  if (!chart) return <EmptyState title="No activity in this period" />;

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[34rem] h-56">
        <Line data={chart.data} options={chart.options} />
      </div>
    </div>
  );
}

export default ChatVolumeChart;
